import React, { useEffect } from 'react';
import { Component, Session, TestCase, BugUrl, PRFeedback, Feature, Platform } from '../../types';
import { formatDate } from '../../utils/date';
import { AppMessage } from '../../utils/messages';

interface ComponentDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  component: Component | null;
  session: Session;
  testCases: TestCase[];
  features: Feature[];
  projectCode: string | null | undefined;
  onPlatformTestedToggle: (componentId: string, platform: Platform) => void | Promise<void>;
  onBugAdded: (bug: BugUrl) => void | Promise<void>;
  onPRFeedbackAdded: (feedback: PRFeedback) => void | Promise<void>;
  onRemoveBug: (bugUrlIndex: number) => void;
  onRemovePRFeedback: (feedbackId: string) => void;
  onEditPRFeedback: (feedback: PRFeedback) => void;
  onViewTestCase: (testCase: TestCase, zIndex?: number) => void;
  onAddBugClick: () => void;
  onAddPRFeedbackClick: () => void;
  showMessage: (message: AppMessage, timeoutMs?: number) => void;
}

const ComponentDetailModal: React.FC<ComponentDetailModalProps> = ({
  isOpen,
  onClose,
  component,
  session,
  testCases,
  features,
  projectCode,
  onPlatformTestedToggle,
  onBugAdded,
  onPRFeedbackAdded,
  onRemoveBug,
  onRemovePRFeedback,
  onEditPRFeedback,
  onViewTestCase,
  onAddBugClick,
  onAddPRFeedbackClick,
  showMessage
}) => {
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  if (!isOpen || !component) return null;

  const sessionPlatforms = session.platforms || (session.platform ? [session.platform] : ['Windows']);
  const componentStatus = session.componentStatuses?.find(s => s.componentId === component.id) || {
    componentId: component.id,
    tested: false,
    testedPlatforms: [],
    bugged: false,
    buggedPlatforms: []
  };
  const testedPlatforms = componentStatus.testedPlatforms || [];
  const buggedPlatforms = componentStatus.buggedPlatforms || [];
  const hasBuggedPlatforms = buggedPlatforms.length > 0;

  const componentTestCases = testCases.filter(tc => tc.componentId === component.id);
  const linkedBugs = (session.bugUrls || []).filter(bugUrl => {
    if (typeof bugUrl === 'string') return false;
    return (bugUrl.componentId || bugUrl.subDepId) === component.id;
  });
  const componentFeedbacks = (session.prFeedbacks || []).filter(feedback => 
    feedback.componentId === component.id
  );

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return '#dc3545';
      case 'high': return '#fd7e14';
      case 'medium': return '#ffc107';
      case 'low': return '#28a745';
      default: return 'var(--text-secondary)';
    }
  };

  const getImpactLabel = (impact: string) => {
    switch (impact) {
      case 'production-blocker': return 'Production Blocker';
      case 'user-facing': return 'User-Facing';
      case 'internal': return 'Internal';
      case 'minor-polish': return 'Minor Polish';
      default: return impact;
    }
  };

  const getCommentTypeLabel = (type: string) => {
    switch (type) {
      case 'bug': return 'Bug';
      case 'design-feedback': return 'Design Feedback';
      case 'edge-case': return 'Edge Case';
      case 'usability-issue': return 'Usability Issue';
      default: return type;
    }
  };

  return (
    <div
      className="modal-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
        padding: '2rem',
        overflow: 'hidden',
        pointerEvents: 'auto'
      }}
      onClick={onClose}
      onWheel={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.preventDefault()}
    >
      <div
        className="card"
        onClick={(e) => e.stopPropagation()}
        onWheel={(e) => e.stopPropagation()}
        style={{
          maxWidth: '900px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          position: 'relative',
          backgroundColor: 'var(--surface)',
          zIndex: 2000,
          pointerEvents: 'auto'
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '1.5rem 2rem',
            borderBottom: '2px solid var(--grid-line)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            position: 'sticky',
            top: 0,
            backgroundColor: 'var(--surface)',
            zIndex: 10,
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600', color: 'var(--text-primary)' }}>
              {component.name}
            </h2>
            {hasBuggedPlatforms && (
              <span style={{ 
                padding: '0.375rem 0.75rem',
                backgroundColor: 'var(--danger)',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '0.75rem',
                fontWeight: '600',
                display: 'inline-block'
              }}>
                Has Bugs
              </span>
            )}
          </div>
          <button
            className="btn btn-ghost btn-icon"
            onClick={onClose}
            style={{ 
              fontSize: '1.5rem', 
              padding: '0.5rem',
              color: 'var(--text-secondary)',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = 'var(--text-primary)';
              e.currentTarget.style.backgroundColor = 'var(--surface-elevated)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = 'var(--text-secondary)';
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            ×
          </button>
        </div>

        {/* Content - Two Column Layout */}
        <div style={{ padding: '2rem', display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '2rem' }}>
          {/* Left Column - Platforms & Actions */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {/* Platforms Section */}
            <div style={{ 
              padding: '1.5rem',
              backgroundColor: 'var(--surface-alt)',
              borderRadius: '8px',
              border: '2px solid var(--grid-line)'
            }}>
              <div style={{ 
                fontSize: '0.75rem', 
                fontWeight: '600', 
                marginBottom: '1rem', 
                color: 'var(--text-secondary)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Mark as Tested
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {sessionPlatforms.map(platform => {
                  const isTested = testedPlatforms.includes(platform);
                  return (
                    <button
                      key={platform}
                      type="button"
                      onClick={() => onPlatformTestedToggle(component.id, platform)}
                      style={{
                        padding: '0.75rem 1rem',
                        fontSize: '0.9375rem',
                        border: `2px solid ${isTested ? 'var(--primary)' : 'var(--grid-line)'}`,
                        borderRadius: '6px',
                        backgroundColor: isTested ? 'var(--primary)' : 'var(--surface)',
                        color: isTested ? 'white' : 'var(--text-primary)',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '0.625rem',
                        fontWeight: '600',
                        transition: 'all 0.2s',
                        boxShadow: isTested ? '0 2px 4px rgba(0, 0, 0, 0.1)' : '0 1px 2px rgba(0, 0, 0, 0.05)',
                        width: '100%'
                      }}
                      onMouseEnter={(e) => {
                        if (!isTested) {
                          e.currentTarget.style.borderColor = 'var(--primary)';
                          e.currentTarget.style.backgroundColor = 'var(--primary-transparent-light)';
                          e.currentTarget.style.color = 'var(--primary)';
                          e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.1)';
                          e.currentTarget.style.transform = 'translateY(-1px)';
                        } else {
                          e.currentTarget.style.backgroundColor = 'var(--primary-bright)';
                          e.currentTarget.style.boxShadow = '0 3px 6px rgba(0, 0, 0, 0.15)';
                          e.currentTarget.style.transform = 'translateY(-1px)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isTested) {
                          e.currentTarget.style.borderColor = 'var(--grid-line)';
                          e.currentTarget.style.backgroundColor = 'var(--surface)';
                          e.currentTarget.style.color = 'var(--text-primary)';
                          e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)';
                          e.currentTarget.style.transform = 'translateY(0)';
                        } else {
                          e.currentTarget.style.backgroundColor = 'var(--primary)';
                          e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                          e.currentTarget.style.transform = 'translateY(0)';
                        }
                      }}
                      title={isTested ? `Tested on ${platform} (click to unmark)` : `Mark as tested on ${platform}`}
                    >
                      <span>{platform}</span>
                      {isTested && <span style={{ fontSize: '1.1rem' }}>✓</span>}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Quick Actions */}
            <div style={{ 
              padding: '1.5rem',
              backgroundColor: 'var(--surface-alt)',
              borderRadius: '8px',
              border: '2px solid var(--grid-line)'
            }}>
              <div style={{ 
                fontSize: '0.75rem', 
                fontWeight: '600', 
                marginBottom: '1rem', 
                color: 'var(--text-secondary)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Quick Actions
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <button
                  className="btn btn-primary"
                  onClick={onAddBugClick}
                  style={{ 
                    fontSize: '0.875rem', 
                    padding: '0.75rem 1rem',
                    backgroundColor: 'var(--primary)',
                    color: 'white',
                    border: 'none',
                    fontWeight: '500',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    width: '100%'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--primary-bright)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--primary)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  Add Bug
                </button>
                <button
                  className="btn btn-primary"
                  onClick={onAddPRFeedbackClick}
                  style={{ 
                    fontSize: '0.875rem', 
                    padding: '0.75rem 1rem',
                    backgroundColor: 'var(--primary)',
                    color: 'white',
                    border: 'none',
                    fontWeight: '500',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    width: '100%'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--primary-bright)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--primary)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  Add PR Feedback
                </button>
              </div>
            </div>
          </div>

          {/* Right Column - Test Cases, Bugs, PR Feedback */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {/* Test Cases Section */}
            {componentTestCases.length > 0 && (
              <div style={{ 
                padding: '1.5rem',
                backgroundColor: 'var(--surface-alt)',
                borderRadius: '8px',
                border: '2px solid var(--grid-line)'
              }}>
                <div style={{ 
                  fontSize: '0.75rem', 
                  fontWeight: '600', 
                  marginBottom: '1rem', 
                  color: 'var(--text-secondary)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Test Cases ({componentTestCases.length})
                </div>
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  gap: '0.5rem',
                  backgroundColor: 'var(--surface)',
                  borderRadius: '6px',
                  border: '1px solid var(--grid-line)',
                  padding: '0.75rem',
                  overflow: 'hidden',
                  maxHeight: '300px',
                  overflowY: 'auto'
                }}>
                  {componentTestCases.map((testCase, index) => {
                    const tcDisplayId = testCase.testCaseNumber 
                      ? (projectCode ? `${projectCode}-TC-${testCase.testCaseNumber}` : `TC-${testCase.testCaseNumber}`)
                      : null;
                    const hasSteps = testCase.steps && testCase.steps.length > 0;
                    return (
                      <button
                        key={testCase.id}
                        onClick={() => onViewTestCase(testCase, 3000)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          borderBottom: index < componentTestCases.length - 1 ? '1px solid var(--grid-line)' : 'none',
                          padding: '0.75rem 0.5rem',
                          color: 'var(--text-primary)',
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                          textAlign: 'left',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          borderRadius: '0',
                          transition: 'all 0.2s',
                          fontWeight: '400'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'rgba(0, 212, 255, 0.08)';
                          e.currentTarget.style.color = 'var(--primary)';
                          e.currentTarget.style.paddingLeft = '0.75rem';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = 'var(--text-primary)';
                          e.currentTarget.style.paddingLeft = '0.5rem';
                        }}
                      >
                        {tcDisplayId && (
                          <span style={{
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            padding: '0.15rem 0.4rem',
                            backgroundColor: 'var(--primary)',
                            color: 'white',
                            borderRadius: '3px',
                            fontFamily: 'monospace',
                            flexShrink: 0
                          }}>
                            {tcDisplayId}
                          </span>
                        )}
                        <span style={{ flex: 1, textAlign: 'left' }}>
                          {testCase.title}
                        </span>
                        {hasSteps && (
                          <span style={{ 
                            fontSize: '0.65rem', 
                            padding: '0.25rem 0.5rem', 
                            backgroundColor: 'var(--primary-transparent-light)', 
                            color: 'var(--primary)', 
                            borderRadius: '3px',
                            flexShrink: 0,
                            fontWeight: '500'
                          }}>
                            {testCase.steps!.length} steps
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Bugs Section */}
            <div style={{ 
              padding: '1.5rem',
              backgroundColor: 'var(--surface-alt)',
              borderRadius: '8px',
              border: '2px solid var(--grid-line)'
            }}>
              <div style={{ 
                fontSize: '0.75rem', 
                fontWeight: '600', 
                marginBottom: '1rem', 
                color: 'var(--text-secondary)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Bugs ({linkedBugs.length})
              </div>
              {linkedBugs.length === 0 ? (
                <p style={{ 
                  fontSize: '0.875rem', 
                  color: 'var(--text-secondary)', 
                  fontStyle: 'italic',
                  padding: '1rem',
                  textAlign: 'center',
                  backgroundColor: 'var(--surface)',
                  borderRadius: '6px',
                  border: '1px solid var(--grid-line)'
                }}>
                  No bugs reported for this component yet.
                </p>
              ) : (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  gap: '0.5rem',
                  backgroundColor: 'var(--surface)',
                  borderRadius: '6px',
                  border: '1px solid var(--grid-line)',
                  padding: '0.75rem',
                  overflow: 'hidden',
                  maxHeight: '300px',
                  overflowY: 'auto'
                }}>
                  {linkedBugs.map((bugUrl, index) => {
                    const url = typeof bugUrl === 'string' ? bugUrl : bugUrl.url;
                    const bugComponentId = typeof bugUrl === 'string' ? null : (bugUrl.componentId || bugUrl.subDepId);
                    const stableKey = `${url}-${bugComponentId || 'unknown'}`;
                    const bugUrlIndex = session.bugUrls?.findIndex(b => {
                      if (typeof b === 'string') return b === url;
                      return b.url === url && (b.componentId || b.subDepId) === component.id;
                    }) ?? -1;
                    
                    return (
                      <div key={stableKey} style={{ 
                        display: 'flex', 
                        alignItems: 'flex-start', 
                        gap: '0.75rem',
                        padding: '0.875rem 0.75rem',
                        borderBottom: index < linkedBugs.length - 1 ? '1px solid var(--grid-line)' : 'none',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--surface-elevated)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                      >
                        <div style={{ 
                          display: 'flex', 
                          flexDirection: 'column', 
                          gap: '0.25rem',
                          flex: 1,
                          minWidth: 0
                        }}>
                          <a 
                            href={url} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            style={{ 
                              color: 'var(--primary)',
                              textDecoration: 'none',
                              fontSize: '0.875rem',
                              wordBreak: 'break-all',
                              fontWeight: '500'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                            onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                          >
                            {url}
                          </a>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '0.75rem',
                            flexWrap: 'wrap'
                          }}>
                            {typeof bugUrl !== 'string' && bugUrl.platforms && bugUrl.platforms.length > 0 && (
                              <span style={{ 
                                fontSize: '0.75rem', 
                                color: 'var(--text-secondary)', 
                                whiteSpace: 'nowrap',
                                padding: '0.25rem 0.5rem',
                                backgroundColor: 'var(--surface-elevated)',
                                borderRadius: '4px',
                                border: '1px solid var(--grid-line)'
                              }}>
                                {bugUrl.platforms.join(', ')}
                              </span>
                            )}
                            {typeof bugUrl !== 'string' && bugUrl.createdAt && (
                              <span style={{ 
                                fontSize: '0.75rem', 
                                color: 'var(--text-secondary)'
                              }}>
                                Created: {formatDate(bugUrl.createdAt, 'MMM dd, yyyy h:mm a')}
                              </span>
                            )}
                          </div>
                        </div>
                        {bugUrlIndex >= 0 && (
                          <button
                            className="btn btn-danger"
                            onClick={() => onRemoveBug(bugUrlIndex)}
                            style={{ 
                              flexShrink: 0, 
                              fontSize: '0.75rem', 
                              padding: '0.375rem 0.75rem',
                              backgroundColor: 'var(--danger)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              fontWeight: '500',
                              cursor: 'pointer',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.opacity = '0.9';
                              e.currentTarget.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.opacity = '1';
                              e.currentTarget.style.transform = 'translateY(0)';
                            }}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* PR Feedback Section */}
            <div style={{ 
              padding: '1.5rem',
              backgroundColor: 'var(--surface-alt)',
              borderRadius: '8px',
              border: '2px solid var(--grid-line)'
            }}>
              <div style={{ 
                fontSize: '0.75rem', 
                fontWeight: '600', 
                marginBottom: '1rem', 
                color: 'var(--text-secondary)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                PR Feedback ({componentFeedbacks.length})
              </div>
              {componentFeedbacks.length === 0 ? (
                <p style={{ 
                  fontSize: '0.875rem', 
                  color: 'var(--text-secondary)', 
                  fontStyle: 'italic',
                  padding: '1rem',
                  textAlign: 'center',
                  backgroundColor: 'var(--surface)',
                  borderRadius: '6px',
                  border: '1px solid var(--grid-line)'
                }}>
                  No PR feedback for this component yet.
                </p>
              ) : (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  gap: '0.5rem',
                  backgroundColor: 'var(--surface)',
                  borderRadius: '6px',
                  border: '1px solid var(--grid-line)',
                  padding: '0.75rem',
                  overflow: 'hidden',
                  maxHeight: '400px',
                  overflowY: 'auto'
                }}>
                  {componentFeedbacks.map((feedback, index) => (
                    <div key={feedback.id} style={{ 
                      padding: '1rem 0.75rem',
                      borderBottom: index < componentFeedbacks.length - 1 ? '1px solid var(--grid-line)' : 'none',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--surface-elevated)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                    >
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '0.75rem', 
                        marginBottom: '0.75rem', 
                        flexWrap: 'wrap' 
                      }}>
                        <a 
                          href={feedback.prUrl} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          style={{ 
                            color: 'var(--primary)',
                            textDecoration: 'none',
                            fontSize: '0.875rem',
                            fontWeight: '600',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '4px',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.textDecoration = 'underline';
                            e.currentTarget.style.backgroundColor = 'var(--primary-transparent-light)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.textDecoration = 'none';
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          PR
                        </a>
                        {feedback.commentUrl && (
                          <a 
                            href={feedback.commentUrl} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            style={{ 
                              color: 'var(--primary)',
                              textDecoration: 'none',
                              fontSize: '0.875rem',
                              fontWeight: '600',
                              padding: '0.25rem 0.5rem',
                              borderRadius: '4px',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.textDecoration = 'underline';
                              e.currentTarget.style.backgroundColor = 'var(--primary-transparent-light)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.textDecoration = 'none';
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          >
                            Comment
                          </a>
                        )}
                        <span style={{ 
                          fontSize: '0.75rem', 
                          padding: '0.25rem 0.625rem',
                          borderRadius: '4px',
                          backgroundColor: getSeverityColor(feedback.severity),
                          color: 'white',
                          fontWeight: '600'
                        }}>
                          {feedback.severity.toUpperCase()}
                        </span>
                        <span style={{ 
                          fontSize: '0.75rem', 
                          color: 'var(--text-secondary)',
                          padding: '0.25rem 0.625rem',
                          backgroundColor: 'var(--surface-elevated)',
                          borderRadius: '4px',
                          border: '1px solid var(--grid-line)',
                          fontWeight: '500'
                        }}>
                          {getImpactLabel(feedback.impact)}
                        </span>
                        <span style={{ 
                          fontSize: '0.75rem', 
                          color: 'var(--text-secondary)',
                          fontWeight: '500'
                        }}>
                          {getCommentTypeLabel(feedback.commentType)}
                        </span>
                      </div>
                      {feedback.description && (
                        <div style={{ 
                          fontSize: '0.875rem', 
                          color: 'var(--text-primary)',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          marginBottom: '0.75rem',
                          lineHeight: '1.5',
                          padding: '0.75rem',
                          backgroundColor: 'var(--surface-elevated)',
                          borderRadius: '4px',
                          border: '1px solid var(--grid-line)'
                        }}>
                          {feedback.description}
                        </div>
                      )}
                      <div style={{ 
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <div style={{ 
                          fontSize: '0.75rem', 
                          color: 'var(--text-secondary)',
                          fontWeight: '500'
                        }}>
                          {formatDate(feedback.createdAt, 'MMM dd, yyyy h:mm a')}
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          className="btn btn-secondary"
                          onClick={() => {
                            onEditPRFeedback(feedback);
                            onClose();
                          }}
                          style={{
                              fontSize: '0.75rem', 
                              padding: '0.375rem 0.75rem',
                              backgroundColor: 'var(--surface-elevated)',
                              border: '1px solid var(--grid-line)',
                              color: 'var(--text-primary)',
                              borderRadius: '4px',
                              fontWeight: '500',
                              cursor: 'pointer',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.borderColor = 'var(--primary)';
                              e.currentTarget.style.color = 'var(--primary)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.borderColor = 'var(--grid-line)';
                              e.currentTarget.style.color = 'var(--text-primary)';
                            }}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-danger"
                            onClick={() => onRemovePRFeedback(feedback.id)}
                            style={{ 
                              fontSize: '0.75rem', 
                              padding: '0.375rem 0.75rem',
                              backgroundColor: 'var(--danger)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              fontWeight: '500',
                              cursor: 'pointer',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.opacity = '0.9';
                              e.currentTarget.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.opacity = '1';
                              e.currentTarget.style.transform = 'translateY(0)';
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComponentDetailModal;
